/** @jsx isolateComponent */

import { css } from '@emotion/core';
import React, { Fragment } from 'react';

import { FlexLayout } from '@zapier/common-components';
import { Size } from '@zapier/design-system-revenue/src/types';
import { Button, Img, Spacer, Surface, Text } from '@zapier/design-system';
import { isolateComponent, responsiveStylesFn } from '@zapier/common-utils';

type CTAProps = {
  href?: string;
  onClick?: (e: any) => void;
  text: string;
};

type UpsellProps = {
  /** The text to display directly below the title */
  description: string;
  /** The image URL */
  imageSrc: string;
  /** Renders the component if false */
  isLoading?: boolean;
  /** The text to display above the title */
  preTitle?: string;
  /** The properties of the leftmost CTA button */
  primaryCta: CTAProps;
  /** The properties of the rightmost CTA button */
  secondaryCta: CTAProps;
  /** The viewport size */
  size: Size;
  /** The text to display as the main title */
  title: string;
  /** The children of the component. */
  children?: React.ReactNode;
};

const rootStyles = responsiveStylesFn({
  small: css`
    border-radius: 10px;
    overflow: hidden;
    position: relative;
  `,
});

const imageStyles = css`
  display: block;
  position: absolute;
  top: 0;
  right: -3%;
  width: 49%;
`;

const innerStyles = responsiveStylesFn({
  small: css`
    padding: 40px;
  `,
  large: css`
    /**
     * This stops the image from appearing in front of the text inside the span.
     * See https://philipwalton.com/articles/what-no-one-told-you-about-z-index/
     */
    opacity: 0.99;
    width: 65%;
  `,
});

export const Upsell = ({
  children,
  description,
  imageSrc,
  isLoading = false,
  preTitle,
  primaryCta,
  secondaryCta,
  size,
  title,
}: UpsellProps) =>
  isLoading ? null : (
    <Surface shadow="elevation10">
      <div css={rootStyles(size)}>
        {size === 'large' && (
          <div css={imageStyles}>
            <Img alt="company" src={imageSrc} />
          </div>
        )}

        <div css={innerStyles(size)}>
          {preTitle && (
            <Fragment>
              <Text color="grey700" type="bodyText900">
                {preTitle}
              </Text>

              <Spacer height={10} />
            </Fragment>
          )}

          <Text tag="h2" type="subtitle900">
            {title}
          </Text>

          <Spacer height={4} />

          <Text color="grey700" tag="p" type="bodyText800">
            {description}
          </Text>

          {children && (
            <Fragment>
              <Spacer height={20} />
              {children}
            </Fragment>
          )}

          <Spacer height={30} />

          <FlexLayout grow={1} gutter="normal" justify="center" shrink={0}>
            <Button
              casing="uppercase"
              color="black"
              href={primaryCta.href}
              isFullWidth={true}
              onClick={primaryCta.onClick}
            >
              {primaryCta.text}
            </Button>

            <Button
              casing="uppercase"
              color="greyAlt"
              href={secondaryCta.href}
              isFullWidth={true}
              onClick={secondaryCta.onClick}
            >
              {secondaryCta.text}
            </Button>
          </FlexLayout>
        </div>
      </div>
    </Surface>
  );
